import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Bell, X, Check, CheckCheck, Info, AlertTriangle, AlertCircle, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const bellRingStyle = `
@keyframes bellRing {
  0%,100%{transform:rotate(0)}
  10%,30%,50%,70%,90%{transform:rotate(-8deg)}
  20%,40%,60%,80%{transform:rotate(8deg)}
}
`;

const TYPE_CONFIG = {
  info:     { icon: Info,          color: 'var(--accent)',  bg: 'var(--accent-light)' },
  warning:  { icon: AlertTriangle, color: 'var(--amber)',   bg: 'rgba(245,158,11,0.1)' },
  error:    { icon: AlertCircle,   color: 'var(--red)',     bg: 'rgba(239,68,68,0.1)' },
  success:  { icon: Check,         color: 'var(--green)',   bg: 'rgba(34,197,94,0.1)' },
  reminder: { icon: Calendar,      color: '#8b5cf6',        bg: 'rgba(139,92,246,0.1)' },
};

export default function NotificationBell({ staffId }) {
  const [notifs, setNotifs]     = useState([]);
  const [unread, setUnread]     = useState(0);
  const [open, setOpen]         = useState(false);
  useEffect(()=>{ const s=document.createElement('style'); s.textContent='@keyframes bellRing{0%,100%{transform:rotate(0)}15%,45%,75%{transform:rotate(-12deg)}30%,60%,90%{transform:rotate(12deg)}}'; document.head.appendChild(s); return()=>s.remove(); },[]);
  const [loading, setLoading]   = useState(false);
  const panelRef                = useRef(null);
  const notifsRef               = useRef([]);
  const navigate                = useNavigate();

  useEffect(() => {
    if (!staffId) return;
    loadNotifs();

    // Use unique channel name to avoid conflicts when component mounts multiple times
    const channelName = `notifications-${staffId}-${Math.random().toString(36).slice(2)}`;
    const sub = supabase
      .channel(channelName)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications' }, () => loadNotifs())
      .subscribe();

    return () => {
      supabase.removeChannel(sub);
    };
  }, [staffId]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function loadNotifs() {
    if (!staffId) return;
    if (notifsRef.current.length === 0) setLoading(true); // spinner only on first load — no flicker on live refresh
    // Global notifications (no staff_id) + the ones addressed to THIS staff member only
    let { data: allNotifs, error: nErr } = await supabase
      .from('notifications')
      .select('*')
      .or(`staff_id.is.null,staff_id.eq.${staffId}`)
      .order('created_at', { ascending: false })
      .limit(50);
    if (nErr) {
      // staff_id column missing / filter rejected — fall back to the plain list so the bell still works
      const r = await supabase.from('notifications').select('*').order('created_at', { ascending: false }).limit(50);
      allNotifs = r.data;
    }

    // Get reads for this staff
    const { data: reads } = await supabase
      .from('notification_reads')
      .select('notification_id')
      .eq('staff_id', staffId);

    const readIds = new Set((reads || []).map(r => r.notification_id));
    const enriched = (allNotifs || []).map(n => ({ ...n, isRead: readIds.has(n.id) }));
    notifsRef.current = enriched;
    setNotifs(enriched);
    setUnread(enriched.filter(n => !n.isRead).length);
    setLoading(false);
  }

  async function markRead(notifId) {
    const { error } = await supabase.from('notification_reads').upsert({ notification_id: notifId, staff_id: staffId }, { onConflict: 'notification_id,staff_id' });
    if (error) { console.error('Mark read failed:', error); return; }
    setNotifs(prev => prev.map(n => n.id === notifId ? { ...n, isRead: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  }

  async function markAllRead() {
    const unreadNotifs = notifs.filter(n => !n.isRead);
    if (!unreadNotifs.length) return;
    const { error } = await supabase.from('notification_reads')
      .upsert(unreadNotifs.map(n => ({ notification_id: n.id, staff_id: staffId })), { onConflict: 'notification_id,staff_id' });
    if (error) { console.error('Mark all read failed:', error); return; }
    setNotifs(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnread(0);
  }

  function handleNotifClick(notif) {
    if (!notif.isRead) markRead(notif.id);
    if (notif.link) { navigate(notif.link); setOpen(false); }
  }

  function timeAgo(ts) {
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (isNaN(mins) || mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  }

  return (
    <div ref={panelRef} style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{ position: 'relative', background: unread>0?'var(--accent)':'rgba(255,255,255,0.12)', border: 'none', cursor: 'pointer', padding: '7px 9px', borderRadius: 10, color: unread>0?'#fff':'var(--text)', display: 'flex', alignItems: 'center', boxShadow: unread>0?'0 2px 8px rgba(0,0,0,0.2)':'none', transition:'all 0.2s' }}
        title="Notifications"
      >
        <Bell size={22} style={{animation:unread>0?'bellRing 1s ease infinite':undefined}}/>
        {unread > 0 && (
          <span style={{ position: 'absolute', top: 2, right: 2, background: 'var(--red)', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div style={{ position: 'fixed', bottom: 70, left: 0, width: 360, maxHeight: '70vh', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', zIndex: 9999, overflow: 'hidden', display: 'flex', flexDirection: 'column', margin: '0 8px' }}>
          {/* Header */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>🔔 Notifications {unread > 0 && <span style={{ background: 'var(--red)', color: '#fff', fontSize: 10, borderRadius: 10, padding: '1px 6px', marginLeft: 6 }}>{unread}</span>}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {unread > 0 && (
                <button onClick={markAllRead} className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} title="Mark all read">
                  <CheckCheck size={13} /> All read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="btn btn-ghost btn-sm"><X size={13} /></button>
            </div>
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <div style={{ padding: 24, textAlign: 'center' }}><div className="spinner" /></div>
            ) : notifs.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🔕</div>
                <div style={{ fontSize: 13 }}>No notifications yet</div>
              </div>
            ) : notifs.map(n => {
              const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.info;
              const Icon = cfg.icon;
              return (
                <div
                  key={n.id}
                  onClick={() => handleNotifClick(n)}
                  style={{ display: 'flex', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)', cursor: n.link ? 'pointer' : 'default', background: n.isRead ? 'transparent' : 'rgba(0,176,155,0.04)', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                  onMouseLeave={e => e.currentTarget.style.background = n.isRead ? 'transparent' : 'rgba(0,176,155,0.04)'}
                >
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    <Icon size={15} color={cfg.color} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: n.isRead ? 400 : 600, color: 'var(--text)', lineHeight: 1.4 }}>{n.title}</div>
                    {n.message && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, lineHeight: 1.4 }}>{n.message}</div>}
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{timeAgo(n.created_at)}</div>
                  </div>
                  {!n.isRead && (
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0, marginTop: 6 }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
